import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type BackOverride = (() => boolean) | null;

type StrongerNudgeBackOverrideContextValue = {
  backOverride: BackOverride;
  setBackOverride: (override: BackOverride) => void;
};

const StrongerNudgeBackOverrideContext =
  createContext<StrongerNudgeBackOverrideContextValue | null>(null);

export function StrongerNudgeBackOverrideProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [backOverride, setBackOverrideState] = useState<BackOverride>(null);

  const setBackOverride = useCallback((override: BackOverride) => {
    setBackOverrideState(() => override);
  }, []);

  const value = useMemo(
    () => ({ backOverride, setBackOverride }),
    [backOverride, setBackOverride],
  );

  return (
    <StrongerNudgeBackOverrideContext.Provider value={value}>
      {children}
    </StrongerNudgeBackOverrideContext.Provider>
  );
}

export function useStrongerNudgeBackOverride(): BackOverride {
  return useContext(StrongerNudgeBackOverrideContext)?.backOverride ?? null;
}

export function useRegisterStrongerNudgeBackOverride(
  handler: () => boolean,
  deps: React.DependencyList,
) {
  const context = useContext(StrongerNudgeBackOverrideContext);

  React.useEffect(() => {
    if (!context) return;

    context.setBackOverride(handler);
    return () => context.setBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies stage-specific deps
  }, deps);
}
